import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Layers, Target, Users, Zap, Mail, CreditCard, Share2, LineChart, Settings, Bell, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const Features = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Features - Validate Your Startup Ideas Without Writing Code"
        description="Discover our powerful features for validating startup ideas, from fake door testing to analytics and insights. Get real customer feedback before you build."
        keywords="startup validation, fake door testing, product testing, product validation, startup idea validation, MVP testing"
        canonicalUrl="/features"
        ogTitle="Powerful Features to Validate Your Startup Idea | WillTheyConvert"
        ogDescription="Test your business ideas without writing code. Our platform helps you measure real demand, collect emails, and get feedback before investing in development."
        ogImage="/images/og-image.jpg"
      />

      {/* Hero Section */}
      <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            Features
          </h1>
          <p className="max-w-2xl mx-auto mt-6 text-xl text-gray-500">
            Everything you need to validate your startup ideas
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Feature 1: Fake Door Testing */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lightbulb className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Fake Door Testing</h2>
            </div>
            <p className="text-gray-500">
              Test market demand for your features before building them. Create landing pages
              that look and feel real, measure interest, and make data-driven decisions.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                <span>Quick setup with our templates</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                <span>A/B testing capabilities</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                <span>Real-time conversion tracking</span>
              </li>
            </ul>
          </div>

          {/* Feature 2: Analytics */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Advanced Analytics</h2>
            </div>
            <p className="text-gray-500">
              Get deep insights into how users interact with your fake doors. Track views,
              clicks, and conversions to understand what resonates with your audience.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                <span>Detailed conversion metrics</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                <span>User behavior analysis</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                <span>Custom event tracking</span>
              </li>
            </ul>
          </div>

          {/* Feature 3: Audience Building */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Audience Building</h2>
            </div>
            <p className="text-gray-500">
              Build an engaged audience before launch. Capture emails, segment users, and
              nurture relationships with potential customers.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span>Email capture forms</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span>Audience segmentation</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span>Automated follow-ups</span>
              </li>
            </ul>
          </div>

          {/* Feature 4: Quick Launch */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Quick Launch</h2>
            </div>
            <p className="text-gray-500">
              Launch your tests in minutes, not days. Our intuitive interface and templates
              make it easy to start validating your ideas immediately.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
                <span>Pre-built templates</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
                <span>No-code editor</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
                <span>Instant deployment</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-gray-900">Ready to Start Testing?</h2>
          <p className="mt-4 text-lg text-gray-500">
            Join thousands of founders who validate their ideas with our platform.
          </p>
          <div className="mt-8">
            <Button className="inline-flex items-center px-6 py-3 text-lg" asChild>
              <a href="/signup">
                Get Started <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features; 